// @oasis-permission-inherits: /ltv (ltv:view)
import { createFileRoute } from "@tanstack/react-router";

import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { ensureSession } from "~/modules/auth/credentials";
import { JsonCell } from "~/modules/ltv/components";
import { metaSendAuditQuery } from "~/modules/ltv/queries";

export const Route = createFileRoute("/_dash/ltv/meta-send-audit")({
  component: RouteComponent,
  beforeLoad() {
    ensureSession();
  },
});

function ArrayCell({ value }: { value: string | null | undefined }) {
  if (!value) {
    return <span className="text-muted-foreground">—</span>;
  }
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      if (parsed.length === 0) {
        return <span className="text-muted-foreground">—</span>;
      }
      return <span className="text-xs">{parsed.join(", ")}</span>;
    }
    return <span className="text-xs">{String(parsed)}</span>;
  } catch {
    return <span className="text-xs">{value}</span>;
  }
}

function RouteComponent() {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    metaSendAuditQuery.useInfiniteQuery({ params: {} });

  const rows = data?.pages.flatMap((p) => p.rows) ?? [];

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Meta Send Audit</h1>
      {isLoading && <p className="text-muted-foreground">Loading...</p>}
      {data && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{rows.length} rows loaded</p>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="bg-blue-50">FB Emails</TableHead>
                  <TableHead className="bg-blue-50">FB First Names</TableHead>
                  <TableHead className="bg-blue-50">FB Last Names</TableHead>
                  <TableHead className="bg-blue-50">FB Cities</TableHead>
                  <TableHead className="bg-blue-50">FB States</TableHead>
                  <TableHead className="bg-blue-50">FB Country Codes</TableHead>
                  <TableHead className="bg-blue-50">FB Zip Codes</TableHead>
                  <TableHead className="bg-blue-50">FB IP Address</TableHead>
                  <TableHead className="bg-blue-50">FB MADID</TableHead>
                  <TableHead className="bg-blue-50">FB External IDs</TableHead>
                  <TableHead className="bg-blue-50">FB Phones</TableHead>
                  <TableHead className="bg-blue-50">FB Order ID</TableHead>
                  <TableHead className="bg-blue-50">FB Value</TableHead>
                  <TableHead className="bg-blue-50">FB Predicted LTV</TableHead>
                  <TableHead className="bg-blue-50">FB Currency</TableHead>
                  <TableHead className="bg-blue-50">FB FBC</TableHead>
                  <TableHead className="bg-blue-50">FB FBP</TableHead>
                  <TableHead className="bg-blue-50">FB Action Source</TableHead>
                  <TableHead className="bg-blue-50">FB User Agent (App Source Only)</TableHead>
                  <TableHead className="bg-blue-50">FB App OS Version (App Source Only)</TableHead>
                  <TableHead className="bg-blue-50">
                    FB App Device Model (App Source Only)
                  </TableHead>
                  <TableHead className="bg-blue-50">
                    FB App Advertiser Tracking (App Source Only)
                  </TableHead>
                  <TableHead className="bg-blue-50">
                    FB App Application Tracking (App Source Only)
                  </TableHead>
                  <TableHead>Processed At</TableHead>
                  <TableHead>Initial Purchase Event Time</TableHead>
                  <TableHead>User ID</TableHead>
                  <TableHead>Event ID</TableHead>
                  <TableHead>Event Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Skip Reason</TableHead>
                  <TableHead>Dry Run</TableHead>
                  <TableHead>Expected LTV (USD)</TableHead>
                  <TableHead>LTV Features (for debugging)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell className="bg-blue-50">
                      <ArrayCell value={row.fb_emails} />
                    </TableCell>
                    <TableCell className="bg-blue-50">
                      <ArrayCell value={row.fb_first_names} />
                    </TableCell>
                    <TableCell className="bg-blue-50">
                      <ArrayCell value={row.fb_last_names} />
                    </TableCell>
                    <TableCell className="bg-blue-50">
                      <ArrayCell value={row.fb_cities} />
                    </TableCell>
                    <TableCell className="bg-blue-50">
                      <ArrayCell value={row.fb_states} />
                    </TableCell>
                    <TableCell className="bg-blue-50">
                      <ArrayCell value={row.fb_country_codes} />
                    </TableCell>
                    <TableCell className="bg-blue-50">
                      <ArrayCell value={row.fb_zip_codes} />
                    </TableCell>
                    <TableCell className="bg-blue-50 text-xs">
                      {row.fb_client_ip_address ?? "—"}
                    </TableCell>
                    <TableCell className="bg-blue-50 text-xs">{row.fb_madid ?? "—"}</TableCell>
                    <TableCell className="bg-blue-50">
                      <ArrayCell value={row.fb_external_ids} />
                    </TableCell>
                    <TableCell className="bg-blue-50">
                      <ArrayCell value={row.fb_phones} />
                    </TableCell>
                    <TableCell className="bg-blue-50 text-xs">
                      {row.fb_custom_order_id ?? "—"}
                    </TableCell>
                    <TableCell className="bg-blue-50 text-xs">
                      {row.fb_custom_value ?? "—"}
                    </TableCell>
                    <TableCell className="bg-blue-50 text-xs">
                      {row.fb_custom_predicted_ltv ?? "—"}
                    </TableCell>
                    <TableCell className="bg-blue-50 text-xs">
                      {row.fb_custom_currency ?? "—"}
                    </TableCell>
                    <TableCell className="bg-blue-50 text-xs">{row.fb_fbc ?? "—"}</TableCell>
                    <TableCell className="bg-blue-50 text-xs">{row.fb_fbp ?? "—"}</TableCell>
                    <TableCell className="bg-blue-50 text-xs">
                      {row.fb_action_source ?? "—"}
                    </TableCell>
                    <TableCell className="bg-blue-50 text-xs">
                      {row.fb_client_user_agent ?? "—"}
                    </TableCell>
                    <TableCell className="bg-blue-50 text-xs">
                      {row.fb_app_os_version ?? "—"}
                    </TableCell>
                    <TableCell className="bg-blue-50 text-xs">
                      {row.fb_app_device_model ?? "—"}
                    </TableCell>
                    <TableCell className="bg-blue-50 text-xs">
                      {row.fb_app_advertiser_tracking_enabled == null && "—"}
                      {row.fb_app_advertiser_tracking_enabled === true && "Yes"}
                      {row.fb_app_advertiser_tracking_enabled === false && "No"}
                    </TableCell>
                    <TableCell className="bg-blue-50 text-xs">
                      {row.fb_app_application_tracking_enabled == null && "—"}
                      {row.fb_app_application_tracking_enabled === true && "Yes"}
                      {row.fb_app_application_tracking_enabled === false && "No"}
                    </TableCell>
                    <TableCell>
                      {row.processed_at ? new Date(row.processed_at.value).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell>
                      {row.capi_event_time != null
                        ? new Date(row.capi_event_time * 1000).toLocaleString()
                        : "—"}
                    </TableCell>
                    <TableCell className="text-xs">{row.user_id ?? "—"}</TableCell>
                    <TableCell className="text-xs">{row.capi_event_id ?? "—"}</TableCell>
                    <TableCell>{row.capi_event_name ?? "—"}</TableCell>
                    <TableCell>{row.status ?? "—"}</TableCell>
                    <TableCell>{row.skip_reason ?? "—"}</TableCell>
                    <TableCell>
                      {row.is_dry_run == null && "—"}
                      {row.is_dry_run === true && "Yes"}
                      {row.is_dry_run === false && "No"}
                    </TableCell>
                    <TableCell>{row.expected_ltv_usd ?? "—"}</TableCell>
                    <TableCell>
                      <JsonCell value={row.features} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {hasNextPage && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {isFetchingNextPage ? "Loading..." : "Load more"}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
